import * as React from "react";
import Linkify from "react-linkify";
import { VideoProject, VideoProjectServices } from "@/services/bedrock";
import { printDateToChat, useSocialCoachSession } from "@/utils";
import { Button, Spinner, Textarea, Badge } from "@/components/ui";
import { MessageSquare, AlertCircle, Send, Copy, Check } from "lucide-react";
import copy from "copy-to-clipboard";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface OwnProps {
  videoProjectId: number;
  messages: Array<Pick<VideoProject.Message, "userName" | "content" | "createdDate" | "userRole">>;
  placeholder?: string;
  noteMessage?: string;
  submitBtnLabel?: string;
  needsAck?: boolean;
  onSuccess: (returnedProject?: VideoProject) => void;
}

const dataElmId = "VideoProjectBubbleDetails";
type Props = OwnProps;

const IntegratedBubbleDetails: React.FC<Props> = ({
  videoProjectId,
  messages,
  placeholder = "Respond here...",
  submitBtnLabel = "Add Message",
  noteMessage,
  needsAck = true,
  onSuccess,
}) => {
  const { isEditor, isAdmin } = useSocialCoachSession();
  const isEditorOrAdmin = isEditor || isAdmin;
  const [messageToSend, setMessageToSend] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [copiedUrl, setCopiedUrl] = React.useState<string | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  const handleCopyUrl = (url: string) => {
    copy(url);
    setCopiedUrl(url);
    setTimeout(() => {
      setCopiedUrl(null);
    }, 2000);
  };

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!messageToSend.trim()) return;
    setLoading(true);
    try {
      if (isEditorOrAdmin) {
        await VideoProjectServices.sendMessage(videoProjectId, messageToSend);
        onSuccess();
      } else {
        const updated = await VideoProjectServices.update(videoProjectId, {
          event: VideoProject.Event.REQUEST_REVISION,
          message: messageToSend,
        });
        onSuccess(updated);
      }
    } catch (e) {
      console.error("Error sending message:", e);
      toast.error("Error sending message");
    } finally {
      setMessageToSend("");
      setLoading(false);
    }
  };

  const linkDecorator = (decoratedHref: string, decoratedText: string, key: string | number) => {
    const truncatedText = decoratedText.length > 30 ? `${decoratedText.substring(0, 27)}...` : decoratedText;
    return (
      <span key={key} className="inline-flex items-center gap-1 select-none">
        <a
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 underline font-medium transition-colors"
          href={decoratedHref}
          title={decoratedText}
        >
          {truncatedText}
        </a>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            handleCopyUrl(decoratedHref);
          }}
          className="p-0.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-all cursor-pointer inline-flex items-center justify-center border-none outline-none"
          title="Copy link to clipboard"
        >
          {copiedUrl === decoratedHref ? (
            <Check className="size-3 text-emerald-600 animate-bounce" />
          ) : (
            <Copy className="size-3" />
          )}
        </button>
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row justify-between items-center w-full select-none">
        <label className="sectionHeader font-semibold text-slate-800 text-sm flex items-center gap-1.5">
          <MessageSquare className="size-4.5 text-sky-600" />
          Messages
          {messages && messages.length > 0 && (
            <Badge variant="outline" size="small" className="bg-sky-50 text-sky-700 border-sky-100 font-bold ml-1">
              {messages.length}
            </Badge>
          )}
          {needsAck && (
            <span
              className="inline-flex items-center justify-center p-1 rounded-full bg-rose-50 border border-rose-100 text-rose-500 animate-pulse shrink-0"
              title="Needs Acknowledgment"
            >
              <AlertCircle className="size-3.5" />
            </span>
          )}
        </label>
      </div>

      <div className="videoProjectContainer flex flex-col gap-4 bg-slate-50/50 p-5 rounded-xl border border-slate-200 w-full">
        <div
          ref={scrollRef}
          className="flex flex-col gap-3.5 max-h-[600px] overflow-y-auto bg-white border border-slate-200/50 p-4 rounded-xl shadow-inner pr-2"
        >
          {messages && messages.length > 0 ? (
            messages.map(({ userName, content, createdDate, userRole }, index) => {
              const isFromEditor = userRole === "EDITOR";
              return (
                <div
                  key={"bubble_" + index}
                  className={cn(
                    "flex flex-col gap-0.5 max-w-[85%]",
                    isFromEditor ? "self-end items-end" : "self-start items-start"
                  )}
                >
                  <div className="text-[10px] text-slate-400 font-medium select-none">
                    <strong className="text-slate-600 font-semibold">{userName}</strong>
                    {" • " + printDateToChat(createdDate)}
                  </div>
                  <div
                    className={cn(
                      "rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap wrap-break-word border select-text text-left",
                      isFromEditor
                        ? "bg-sky-50/50 text-slate-800 border-sky-100 shadow-sm shadow-sky-100/40 rounded-tr-none"
                        : "bg-white text-slate-700 border-slate-300 shadow-2xs rounded-tl-none"
                    )}
                  >
                    <Linkify componentDecorator={linkDecorator}>{content}</Linkify>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-slate-400 text-xs py-6 text-center select-none">No messages yet.</div>
          )}
        </div>

        <div className="flex flex-col gap-2 pt-3 border-t border-slate-100">
          <div className="relative flex items-center w-full">
            <Textarea
              data-elm-id={dataElmId + "MessageToSend"}
              id="messageToSend"
              placeholder={placeholder}
              rows={2}
              className="pr-10 bg-white"
              value={messageToSend}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  if (isEditorOrAdmin) {
                    e.preventDefault();
                    sendMessage();
                  }
                }
              }}
              onChange={e => setMessageToSend(e.target.value)}
              disabled={loading}
            />
            {isEditorOrAdmin && (
              <button
                type="button"
                onClick={sendMessage}
                disabled={loading || !messageToSend.trim()}
                className="absolute right-3 text-slate-400 hover:text-indigo-600 disabled:opacity-40 disabled:hover:text-slate-400 transition-colors cursor-pointer"
                title="Send message"
              >
                {loading ? <Spinner className="size-4.5" /> : <Send className="size-4.5" />}
              </button>
            )}
          </div>
          {noteMessage && <p className="text-xs text-slate-400 text-left select-none">{noteMessage}</p>}
          {!isEditorOrAdmin && (
            <div className="flex justify-end w-full">
              <Button
                data-elm-id={dataElmId + "SubmitBtn"}
                onClick={sendMessage}
                disabled={messageToSend.trim() === "" || loading}
                loading={loading}
                variant="default"
              >
                {submitBtnLabel}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const VideoProjectBubbleDetails = IntegratedBubbleDetails;
